/**
 * Says why the controls on this screen are inert.
 *
 * Every settings surface in the app already respects `canManage` — it either disables the
 * inputs or drops the buttons entirely. What none of them did was say so. An agent opening
 * Automation, or Team, or Integrations saw a page of greyed-out fields with no reason given and
 * no next step: indistinguishable from a screen that has failed to load, or from the product
 * being broken. Two support messages later somebody explains it is a role thing.
 *
 * So: one line, at the top, naming what cannot be changed and who can change it. Deliberately
 * not a warning tone — nothing has gone wrong, this is the account working as configured — and
 * deliberately one sentence, because a viewer does not need a paragraph to understand that they
 * are a viewer.
 *
 * The wording avoids a pronoun for `what` on purpose, so a caller can pass either "automation
 * rules" or "this workspace's AI provider" without the sentence having to agree with it.
 *
 * `onTeamScreen` exists because the closing advice — "an owner or admin can give you access from
 * Team" — is circular when the reader is already looking at Team. There it points at the people
 * instead of the place.
 */
export function ReadOnlyNotice({ what, onTeamScreen }: { what: string; onTeamScreen?: boolean }) {
  return (
    // `role="note"`, not `role="status"`: this is static context present at first paint, not a
    // live region announcing a change. A polite live region here would mostly go unannounced and
    // would dilute the ones that matter.
    <p className="cad-note" role="note">
      Read-only: your role cannot change {what}.{' '}
      {onTeamScreen
        ? 'Ask an owner or an admin of this workspace to change your role.'
        : 'An owner or admin can give you access from Team.'}
    </p>
  )
}
